import { ActionIcon, AppShell, Group, ScrollArea, Tooltip, useComputedColorScheme } from '@mantine/core';
import { useDisclosure, useLocalStorage, useMediaQuery } from '@mantine/hooks';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlignLeft, FiArrowRight, FiMaximize, FiMenu } from 'react-icons/fi';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useKiosk } from '@web/lib/kiosk';
import { NAV_SECTIONS, type NavItem } from '../navigation';
import { tokens } from '../theme';
import classes from './AppLayout.module.css';
import { LanguageMenu } from './LanguageMenu';

export interface AppLayoutProps {
  /** Menu items the current user may see, already filtered. */
  items: NavItem[];
  /** Resolves an item to an absolute path (org items live under the active org). */
  hrefFor: (item: NavItem) => string;
  homeHref: string;
  /** Left side of the top bar after the menu toggle (org switcher, breadcrumb…). */
  headerStart?: ReactNode;
  /** Right side of the top bar before the language menu (live indicator, user menu…). */
  headerEnd?: ReactNode;
  /** Full-width banners above the page (impersonation, suspended org…). */
  banners?: ReactNode;
  /** Shown under the menu when it is expanded ("My wells"). */
  navExtra?: ReactNode;
  /** The organization at the top of the menu, in place of TwinLink, when it has a logo. */
  brand?: { name: string; logoUrl: string | null };
}

export function AppLayout({ items, hrefFor, homeHref, headerStart, headerEnd, banners, navExtra, brand }: AppLayoutProps) {
  const { t } = useTranslation();
  const dark = useComputedColorScheme('light') === 'dark';
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [mobileOpen, mobile] = useDisclosure(false);
  const [collapsed, setCollapsed] = useLocalStorage({ key: 'tl.nav.collapsed', defaultValue: false });
  const rail = collapsed && !isMobile;
  // Kiosk (TV) mode: the page alone, without the top bar and the menu.
  const kiosk = useKiosk((s) => s.active);

  const toggle = () => (isMobile ? mobile.toggle() : setCollapsed((c) => !c));
  const toggleLabel = isMobile ? t('nav.openMenu') : collapsed ? t('nav.expand') : t('nav.collapse');

  const isActive = (item: NavItem) => {
    const href = hrefFor(item);
    if (href === homeHref) return location.pathname === href || location.pathname === `${href}/`;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <AppShell
      layout="alt"
      disabled={kiosk}
      header={{ height: tokens.headerHeight }}
      navbar={{
        width: rail ? tokens.navCollapsedWidth : tokens.navWidth,
        breakpoint: 900,
        collapsed: { mobile: !mobileOpen },
      }}
      padding={0}
      withBorder={false}
      transitionDuration={200}
    >
      <AppShell.Header className={classes.header}>
        <Tooltip label={toggleLabel}>
          <ActionIcon size="lg" onClick={toggle} aria-label={toggleLabel} aria-expanded={isMobile ? mobileOpen : !collapsed}>
            {isMobile ? <FiMenu size={22} /> : rail ? <FiArrowRight size={22} /> : <FiAlignLeft size={22} />}
          </ActionIcon>
        </Tooltip>
        <Group gap="sm" style={{ flex: 1, minWidth: 0 }} wrap="nowrap">
          {headerStart}
        </Group>
        <Group gap={6} wrap="nowrap">
          {headerEnd}
          <Tooltip label={t('shell.fullscreen')}>
            <ActionIcon
              size="lg"
              visibleFrom="sm"
              aria-label={t('shell.fullscreen')}
              onClick={() => {
                if (document.fullscreenElement) void document.exitFullscreen();
                else void document.documentElement.requestFullscreen();
              }}
            >
              <FiMaximize size={18} />
            </ActionIcon>
          </Tooltip>
          <LanguageMenu />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar className={classes.navbar} aria-label={t('nav.main')}>
        {/* The organization's own logo leads; TwinLink moves to the foot of the menu. */}
        {brand?.logoUrl ? (
          <Link to={homeHref} className={classes.brandBlock} data-collapsed={rail} aria-label={brand.name}>
            <img src={brand.logoUrl} alt="" className={rail ? classes.brandLogo : classes.orgBrandLogo} />
          </Link>
        ) : (
          <Link to={homeHref} className={classes.brandBlock} data-collapsed={rail} aria-label={t('app.name')}>
            <img src="/brand/twinlink-logo.png" alt="" className={classes.brandLogo} />
            {!rail && <span className={classes.brandWord}>{t('app.name')}</span>}
          </Link>
        )}
        <ScrollArea className={classes.navScroll} type="hover" scrollbarSize={6}>
          <div>
            {NAV_SECTIONS.map((section) => {
              const sectionItems = items.filter((i) => i.section === section);
              if (sectionItems.length === 0) return null;
              return (
                <div key={section}>
                  {rail ? (
                    <div className={classes.captionRule} />
                  ) : (
                    <div className={`tl-caption ${classes.caption}`}>{t(`nav.sections.${section}`)}</div>
                  )}
                  {sectionItems.map((item) => {
                    const Icon = item.icon;
                    const label = t(`nav.${item.key}`);
                    const link = (
                      <NavLink
                        key={item.key}
                        to={hrefFor(item)}
                        className={classes.link}
                        data-active={isActive(item)}
                        data-collapsed={rail}
                        aria-current={isActive(item) ? 'page' : undefined}
                        aria-label={rail ? label : undefined}
                        onClick={mobile.close}
                      >
                        <Icon className={classes.linkIcon} aria-hidden />
                        {!rail && <span>{label}</span>}
                      </NavLink>
                    );
                    return rail ? (
                      <Tooltip key={item.key} label={label} position="right">
                        {link}
                      </Tooltip>
                    ) : (
                      link
                    );
                  })}
                </div>
              );
            })}
            {!rail && navExtra}
          </div>
        </ScrollArea>
        <div className={classes.navFooter} data-collapsed={rail}>
          <div className={classes.poweredBy}>
            <img src="/brand/twinlink-logo.png" alt="" className={classes.footLogo} />
            <span className={classes.footWord}>{t('app.name')}</span>
          </div>
        </div>
      </AppShell.Navbar>

      <AppShell.Main className={classes.main}>
        {banners}
        <div className={classes.content}>
          <Outlet />
        </div>
        {!kiosk && (
          <footer className={classes.footer}>
            <span>{t('app.copyright')}</span>
            <img src={dark ? '/brand/smardac-logo-light.png' : '/brand/smardac-logo-dark.png'} alt="SmarDac" className={classes.smardacLogo} />
            <span>{t('app.copyrightYear', { year: new Date().getFullYear() })}</span>
          </footer>
        )}
      </AppShell.Main>
    </AppShell>
  );
}
